import { useState, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import { makeStyles, tokens, Toaster, useId } from '@fluentui/react-components';
import { NavMenu } from '@shared/components/navigation/NavMenu';
import { Header } from '@shared/components/navigation/Header';
import { TenantBanner } from '@shared/components/navigation/TenantBanner';
import { useActiveChromeColors } from '@app/providers/ThemeProvider';
import { useRouteTracking } from '@shared/telemetry';
import { useNotificationHub } from '@shared/hooks/useNotificationHub';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  container: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
    position: 'relative',
  },
  sidebar: {
    width: '250px',
    flexShrink: 0,
    borderRight: `1px solid ${tokens.colorNeutralStroke1}`,
    overflowY: 'auto',
    '@media (max-width: 768px)': {
      display: 'none',
    },
  },
  // Mobile sidebar overlay
  sidebarMobile: {
    display: 'none',
    '@media (max-width: 768px)': {
      display: 'flex',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '280px',
      height: '100%',
      zIndex: 1000,
      boxShadow: tokens.shadow16,
      borderRight: `1px solid ${tokens.colorNeutralStroke1}`,
      overflowY: 'auto',
    },
  },
  // Backdrop for mobile menu
  backdrop: {
    display: 'none',
    '@media (max-width: 768px)': {
      display: 'block',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      zIndex: 999,
    },
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  },
  main: {
    flex: 1,
    padding: tokens.spacingHorizontalXL,
    '@media (max-width: 768px)': {
      padding: tokens.spacingHorizontalM,
    },
  },
});

export function MainLayout() {
  const styles = useStyles();
  const toasterId = useId('toaster');
  const { navigationColor, navigationTextColor } = useActiveChromeColors();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Track route changes for telemetry
  useRouteTracking();

  // Connect to notification hub for real-time toasts (TokenCreated, VisitorCheckedIn, etc.)
  useNotificationHub();

  const handleToggleMobileMenu = useCallback(() => {
    setMobileMenuOpen((prev) => !prev);
  }, []);

  const handleCloseMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  return (
    <div className={styles.root}>
      <Header onToggleMobileMenu={handleToggleMobileMenu} />
      <div className={styles.container}>
        {/* Desktop sidebar */}
        <nav
          className={styles.sidebar}
          style={{
            backgroundColor: navigationColor,
            color: navigationTextColor,
          }}
        >
          <NavMenu />
        </nav>

        {/* Mobile sidebar with backdrop */}
        {mobileMenuOpen && (
          <>
            <div className={styles.backdrop} onClick={handleCloseMobileMenu} />
            <nav
              className={styles.sidebarMobile}
              style={{
                backgroundColor: navigationColor || tokens.colorNeutralBackground1,
                color: navigationTextColor,
              }}
              onClick={handleCloseMobileMenu}
            >
              <NavMenu />
            </nav>
          </>
        )}

        <div className={styles.content}>
          <main className={styles.main}>
            <TenantBanner />
            <Outlet />
          </main>
        </div>
      </div>
      <Toaster toasterId={toasterId} position="top-end" />
    </div>
  );
}

export default MainLayout;
